import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { eventBus } from './eventEmitter/eventBus';
import { extractNormalizedTicksFromMessage } from './normalizeTick';
import { buildLiveStreamWebSocketUrl } from './socketUrl';
import { registerAuthSocketCloser } from '../auth/authSessionTermination';
import {
  ITEM_REALTIME_STATE,
  mapBrokerStatusToItemState,
  extractBrokerResultsFromMessage,
  resolveBrokerStatusField,
} from '../../utils/watchlistRealtimeState';
import {
  applyGenericSubscribeAction,
  clearActiveWatchlistDesired,
  clearManualModalDesired,
  createDesiredSubscriptionState,
  disableOpenTradesDesired,
  enableOpenTradesDesired,
  getDesiredGlobalTokens,
  getReplayPayloads,
  parsePositiveSafeInteger,
  setActiveWatchlistDesired,
  setGlobalDesired,
  setManualModalDesired,
  SOURCE_KEYS,
  snapshotDesiredSources,
} from './desiredSubscriptions';
import {
  TRADE_CLOSED_BUS_EVENT,
  createTradeCloseIdempotencyStore,
  normalizeTradeCloseMessage,
} from './tradeCloseEvents';

const WebSocketContext = createContext(null);
export const useWebSocket = () => useContext(WebSocketContext);

export const CONNECTION_STATUS = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  MARKET_CLOSED: 'market_closed',
  LOGGED_OUT: 'logged_out',
  DISCONNECTED: 'disconnected',
  FAILED: 'failed',
};

export const SUBSCRIPTION_STATE = {
  WAITING_FOR_SUBSCRIPTION: 'waiting_for_subscription',
  SUBSCRIBED_WAITING_FOR_TICK: 'subscribed_waiting_for_tick',
  LIVE: 'live',
  STALE: 'stale',
  SUBSCRIPTION_FAILED: 'subscription_failed',
  REJECTED: 'rejected',
};

const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;
const HEARTBEAT_INTERVAL_MS = 2500;
const LOGOUT_CLOSE_TIMEOUT_MS = 250;

const getLatestJwt = () => {
  const token = localStorage.getItem('token');
  if (typeof token !== 'string') return null;

  const trimmedToken = token.trim();
  if (!trimmedToken || trimmedToken === 'undefined' || trimmedToken === 'null') {
    return null;
  }

  return trimmedToken;
};

const isConnectionReadyMessage = (message) => (
  message?.type === 'connection.ready' ||
  (message?.type === 'success' && message?.message === 'Connection Successful')
);

export const WebSocketProvider = ({ children }) => {
  const tickMapRef = useRef({});
  const socketRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const desiredSourcesRef = useRef(createDesiredSubscriptionState());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const connectionGenerationRef = useRef(0);
  const lifecycleGenerationRef = useRef(0);
  const disposedRef = useRef(false);
  const logoutRef = useRef(false);
  const marketClosedRef = useRef(false);
  const logoutPromiseRef = useRef(null);
  const logoutSocketRef = useRef(null);
  const logoutCloseTimeoutRef = useRef(null);
  const logoutResolveRef = useRef(null);
  const logoutFinishGenerationRef = useRef(0);
  const connectWebSocketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.IDLE);
  const [subscriptionStatus, setSubscriptionStatus] = useState(SUBSCRIPTION_STATE.WAITING_FOR_SUBSCRIPTION);
  const [activeWatchlistTokens, setActiveWatchlistTokens] = useState([]);
  const symbolStatusesRef = useRef({});
  const [symbolStatuses, setSymbolStatuses] = useState({});
  const processedTradeClosesRef = useRef(createTradeCloseIdempotencyStore());

  const updateSymbolStatus = useCallback((token, nextStatus, extra = {}) => {
    const tok = Number(token);
    if (!Number.isSafeInteger(tok) || tok <= 0) return;

    const current = symbolStatusesRef.current[tok];
    if (current?.state === nextStatus && current?.requestId === extra.requestId && current?.message === extra.message) return;

    symbolStatusesRef.current = {
      ...symbolStatusesRef.current,
      [tok]: {
        state: nextStatus,
        ...extra,
        lastUpdated: Date.now(),
      },
    };
    setSymbolStatuses({ ...symbolStatusesRef.current });
  }, []);

  const registerPendingSymbol = useCallback((token, state = ITEM_REALTIME_STATE.WAITING_FOR_MEMBERSHIP) => {
    const tok = Number(token);
    if (!Number.isSafeInteger(tok) || tok <= 0) return;
    updateSymbolStatus(tok, state);
  }, [updateSymbolStatus]);

  const getSymbolStatus = useCallback((token) => {
    const tok = Number(token);
    if (!Number.isSafeInteger(tok)) return null;
    return symbolStatusesRef.current[tok] || null;
  }, []);

  const getSymbolStatuses = useCallback(() => symbolStatusesRef.current, []);

  const isCurrentConnection = useCallback((generation, ws) => (
    connectionGenerationRef.current === generation && socketRef.current === ws
  ), []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearLogoutCloseTimer = useCallback(() => {
    if (logoutCloseTimeoutRef.current) {
      clearTimeout(logoutCloseTimeoutRef.current);
      logoutCloseTimeoutRef.current = null;
    }
  }, []);

  const finishLogout = useCallback((generation) => {
    if (generation !== logoutFinishGenerationRef.current) return;

    clearLogoutCloseTimer();
    const resolve = logoutResolveRef.current;
    logoutResolveRef.current = null;
    logoutPromiseRef.current = null;
    logoutSocketRef.current = null;
    if (resolve) resolve();
  }, [clearLogoutCloseTimer]);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimer();

    if (disposedRef.current || logoutRef.current || marketClosedRef.current) return false;

    if (!getLatestJwt()) {
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      return false;
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error('Max reconnect attempts reached. Giving up.');
      setConnectionStatus(CONNECTION_STATUS.FAILED);
      return false;
    }

    setConnectionStatus(CONNECTION_STATUS.RECONNECTING);
    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** reconnectAttemptsRef.current,
      MAX_RECONNECT_DELAY_MS
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      if (disposedRef.current || logoutRef.current || marketClosedRef.current) return;

      if (!getLatestJwt()) {
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        return;
      }

      const latestSocket = socketRef.current;
      if (
        latestSocket &&
        (latestSocket.readyState === WebSocket.CONNECTING || latestSocket.readyState === WebSocket.OPEN)
      ) {
        return;
      }

      reconnectAttemptsRef.current += 1;
      console.warn(`Reconnecting WebSocket (attempt ${reconnectAttemptsRef.current})...`);
      connectWebSocketRef.current?.({ reconnect: true });
    }, delay);

    return true;
  }, [clearReconnectTimer]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const safeSendSocketPayload = useCallback(({
    ws = socketRef.current,
    generation,
    payload,
    closeOnFailure = true,
  }) => {
    if (!payload || !ws) return false;

    if (generation !== undefined && !isCurrentConnection(generation, ws)) {
      return false;
    }

    if (ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    let serializedPayload;
    try {
      serializedPayload = JSON.stringify(payload);
    } catch (e) {
      return false;
    }

    try {
      ws.send(serializedPayload);
      return true;
    } catch (e) {
      if (closeOnFailure && (generation === undefined || isCurrentConnection(generation, ws))) {
        stopHeartbeat();
        if (ws.readyState !== WebSocket.CLOSING && ws.readyState !== WebSocket.CLOSED) {
          try {
            ws.close();
          } catch (closeError) {
            setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
          }
        }
      }
      return false;
    }
  }, [isCurrentConnection, stopHeartbeat]);

  const sendPayload = useCallback((payload) => (
    safeSendSocketPayload({ payload })
  ), [safeSendSocketPayload]);

  const sendDesiredPayload = useCallback((payload) => {
    if (logoutRef.current) return null;
    sendPayload(payload);
    return payload;
  }, [sendPayload]);

  const startHeartbeat = useCallback((ws, generation) => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      if (
        isCurrentConnection(generation, ws) &&
        ws.readyState === WebSocket.OPEN &&
        !logoutRef.current &&
        !marketClosedRef.current &&
        !disposedRef.current
      ) {
        safeSendSocketPayload({
          ws,
          generation,
          payload: { action: 'ping' },
          closeOnFailure: true,
        });
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [isCurrentConnection, safeSendSocketPayload, stopHeartbeat]);

  const replayDesiredSources = useCallback((ws, generation) => (
    getReplayPayloads(desiredSourcesRef.current).every((payload) => (
      safeSendSocketPayload({
        ws,
        generation,
        payload,
        closeOnFailure: true,
      })
    ))
  ), [safeSendSocketPayload]);

  const connectWebSocket = useCallback((options = {}) => {
    const currentSocket = socketRef.current;
    if (
      currentSocket &&
      (currentSocket.readyState === WebSocket.CONNECTING || currentSocket.readyState === WebSocket.OPEN)
    ) {
      return currentSocket;
    }

    if (disposedRef.current || logoutRef.current || marketClosedRef.current) {
      return null;
    }

    const userToken = getLatestJwt();
    if (!userToken) {
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      return null;
    }

    clearReconnectTimer();
    const generation = connectionGenerationRef.current + 1;
    connectionGenerationRef.current = generation;
    setConnectionStatus(options.reconnect ? CONNECTION_STATUS.RECONNECTING : CONNECTION_STATUS.CONNECTING);
    let ws;
    try {
      ws = new WebSocket(buildLiveStreamWebSocketUrl(userToken));
    } catch (e) {
      socketRef.current = null;
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      console.error('WebSocket construction failed.');
      scheduleReconnect();
      return null;
    }
    socketRef.current = ws;

    ws.onopen = () => {
      if (!isCurrentConnection(generation, ws)) return;
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);
    };

    ws.onmessage = (e) => {
      if (!isCurrentConnection(generation, ws)) return;

      try {
        const message = JSON.parse(e.data);

        if (message?.type === 'info' && message?.message === 'Market is closed') {
          marketClosedRef.current = true;
          clearReconnectTimer();
          stopHeartbeat();
          setConnectionStatus(CONNECTION_STATUS.MARKET_CLOSED);
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
          return;
        }

        if (isConnectionReadyMessage(message)) {
          if (!replayDesiredSources(ws, generation)) {
            setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
            return;
          }
          setConnectionStatus(CONNECTION_STATUS.CONNECTED);
          reconnectAttemptsRef.current = 0;
          startHeartbeat(ws, generation);
          return;
        }

        if (message?.type === 'pong') return;

        const brokerResults = extractBrokerResultsFromMessage(message);
        if (brokerResults.length > 0) {
          brokerResults.forEach(({ token, status, requestId, message: msg }) => {
            const hasTick = Boolean(tickMapRef.current[token] && (tickMapRef.current[token].last_price > 0 || tickMapRef.current[token].price > 0));
            const itemState = mapBrokerStatusToItemState(status, hasTick);
            if (itemState) {
              updateSymbolStatus(token, itemState, { requestId, message: msg });
            }
          });
        }

        if (message?.type === 'subscription_update') {
          if (message.source === 'watchlist_add') {
            const addedTokens = (message.subscribed || message.tokens || []).map(Number).filter((tok) => Number.isSafeInteger(tok) && tok > 0);
            const brokerStatus = resolveBrokerStatusField(message);
            let aggregateState = null;
            addedTokens.forEach((tok) => {
              const hasTick = Boolean(tickMapRef.current[tok] && (tickMapRef.current[tok].last_price > 0 || tickMapRef.current[tok].price > 0));
              const mappedStatus = mapBrokerStatusToItemState(brokerStatus, hasTick);
              const nextState = mappedStatus || (hasTick ? ITEM_REALTIME_STATE.LIVE : ITEM_REALTIME_STATE.WAITING_FOR_SUBSCRIPTION);
              updateSymbolStatus(tok, nextState, { requestId: message.requestId });
              aggregateState = nextState;
            });

            setActiveWatchlistTokens((prev) => Array.from(new Set([...prev, ...addedTokens])));
            if (
              aggregateState === ITEM_REALTIME_STATE.SUBSCRIPTION_FAILED
            ) {
              setSubscriptionStatus(SUBSCRIPTION_STATE.SUBSCRIPTION_FAILED);
            } else if (
              aggregateState === ITEM_REALTIME_STATE.REJECTED
              || aggregateState === ITEM_REALTIME_STATE.REJECTED_EXPIRED
              || aggregateState === ITEM_REALTIME_STATE.REJECTED_INACTIVE
              || aggregateState === ITEM_REALTIME_STATE.REJECTED_INVALID
            ) {
              setSubscriptionStatus(SUBSCRIPTION_STATE.REJECTED);
            } else if (
              aggregateState === ITEM_REALTIME_STATE.LIVE
              || aggregateState === ITEM_REALTIME_STATE.SUBSCRIBED_WAITING_FOR_FIRST_TICK
            ) {
              setSubscriptionStatus(SUBSCRIPTION_STATE.SUBSCRIBED_WAITING_FOR_TICK);
            } else {
              setSubscriptionStatus(SUBSCRIPTION_STATE.WAITING_FOR_SUBSCRIPTION);
            }
            return;
          }

          if (message.source === 'watchlist_remove') {
            const removedTokens = new Set((message.unsubscribed || []).map(Number));
            setActiveWatchlistTokens((prev) => prev.filter((t) => !removedTokens.has(t)));
            const globalTokens = new Set(getDesiredGlobalTokens(desiredSourcesRef.current));
            const openTradeTokens = new Set((desiredSourcesRef.current.get('open_trades')?.payload?.tokens || []).map(Number));
            const manualTokens = new Set((desiredSourcesRef.current.get('manual_modal')?.payload?.token || []).map(Number));

            removedTokens.forEach((tok) => {
              delete symbolStatusesRef.current[tok];
              if (!globalTokens.has(tok) && !openTradeTokens.has(tok) && !manualTokens.has(tok)) {
                delete tickMapRef.current[tok];
              }
            });
            setSymbolStatuses({ ...symbolStatusesRef.current });
            return;
          }

          // Backend global / trades / open_trades_all acks must not be treated as watchlist switches.
          if (message.source === 'global') {
            const resolvedTokens = (message.tokens || message.subscribed || [])
              .map(Number)
              .filter((tok) => Number.isSafeInteger(tok) && tok > 0);
            if (resolvedTokens.length) {
              setGlobalDesired(desiredSourcesRef.current, resolvedTokens);
            }
            return;
          }

          if (message.source === 'trades' || message.source === 'open_trades_all') {
            return;
          }

          const tokens = new Set((message.tokens || []).map(Number));
          const globalTokens = new Set(getDesiredGlobalTokens(desiredSourcesRef.current));
          const openTradeTokens = new Set((desiredSourcesRef.current.get('open_trades')?.payload?.tokens || []).map(Number));
          const manualTokens = new Set((desiredSourcesRef.current.get('manual_modal')?.payload?.token || []).map(Number));

          const switchBrokerStatus = resolveBrokerStatusField(message);
          tokens.forEach((tok) => {
            const hasTick = Boolean(tickMapRef.current[tok] && (tickMapRef.current[tok].last_price > 0 || tickMapRef.current[tok].price > 0));
            const mappedStatus = mapBrokerStatusToItemState(switchBrokerStatus, hasTick);
            const current = symbolStatusesRef.current[tok]?.state;
            if (!current || current === ITEM_REALTIME_STATE.WAITING_FOR_MEMBERSHIP || current === ITEM_REALTIME_STATE.WAITING_FOR_SUBSCRIPTION) {
              const nextState = mappedStatus || (hasTick ? ITEM_REALTIME_STATE.LIVE : ITEM_REALTIME_STATE.SUBSCRIBED_WAITING_FOR_FIRST_TICK);
              updateSymbolStatus(tok, nextState, { requestId: message.requestId });
            }
          });

          Object.keys(tickMapRef.current).forEach((tokStr) => {
            const tok = Number(tokStr);
            const isWatchlist = tokens.has(tok);
            const isGlobal = globalTokens.has(tok);
            const isOpenTrade = openTradeTokens.has(tok);
            const isManual = manualTokens.has(tok);
            if (!isWatchlist && !isGlobal && !isOpenTrade && !isManual) {
              delete tickMapRef.current[tokStr];
              delete symbolStatusesRef.current[tokStr];
            }
          });
          setSymbolStatuses({ ...symbolStatusesRef.current });

          const nextStatus = message.rejected > 0
            ? SUBSCRIPTION_STATE.REJECTED
            : tokens.size > 0
            ? SUBSCRIPTION_STATE.SUBSCRIBED_WAITING_FOR_TICK
            : SUBSCRIPTION_STATE.WAITING_FOR_SUBSCRIPTION;
          setSubscriptionStatus(nextStatus);
          setActiveWatchlistTokens(Array.from(tokens));
        }

        extractNormalizedTicksFromMessage(message).forEach((tick) => {
          const tok = tick.instrument_token;
          const prev = tickMapRef.current[tok];
          // Skip identical republished ticks (same token/ts/ltp) after re-subscribe storms.
          if (
            prev
            && prev.ts != null
            && tick.ts != null
            && Number(prev.ts) === Number(tick.ts)
            && Number(prev.last_price) === Number(tick.last_price)
          ) {
            return;
          }
          tickMapRef.current[tok] = tick;
          const price = Number(tick.last_price ?? tick.price ?? tick.lastPrice ?? 0);
          if (price > 0) {
            updateSymbolStatus(tok, ITEM_REALTIME_STATE.LIVE);
            setSubscriptionStatus((prevStatus) => (prevStatus === SUBSCRIPTION_STATE.SUBSCRIBED_WAITING_FOR_TICK ? SUBSCRIPTION_STATE.LIVE : prevStatus));
          }
        });

        if (message?.type === 'trade_executed') {
          eventBus.emit('position-update', 'success');
        }

        const closedTrade = normalizeTradeCloseMessage(message);
        if (closedTrade) {
          // Deduplicate trade_closed + legacy trade_sl_hit / trade_tp_hit for the same trade.
          if (processedTradeClosesRef.current.tryClaim(closedTrade.tradeId)) {
            eventBus.emit(TRADE_CLOSED_BUS_EVENT, closedTrade);
          }
        }
      } catch (err) {
        console.error('WebSocket message parsing failed.');
      }
    };

    ws.onerror = (e) => {
      if (!isCurrentConnection(generation, ws)) return;
      console.error('WebSocket connection error.');
      if (!logoutRef.current && !marketClosedRef.current && !disposedRef.current) {
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      }
      if (ws.readyState !== WebSocket.CLOSING && ws.readyState !== WebSocket.CLOSED) {
        ws.close();
      }
    };

    ws.onclose = () => {
      if (!isCurrentConnection(generation, ws)) return;

      stopHeartbeat();
      socketRef.current = null;

      if (logoutRef.current) {
        setConnectionStatus(CONNECTION_STATUS.LOGGED_OUT);
        return;
      }

      if (marketClosedRef.current) {
        setConnectionStatus(CONNECTION_STATUS.MARKET_CLOSED);
        return;
      }

      scheduleReconnect();
    };

    return ws;
  }, [
    clearReconnectTimer,
    isCurrentConnection,
    replayDesiredSources,
    scheduleReconnect,
    startHeartbeat,
    stopHeartbeat,
    updateSymbolStatus,
  ]);

  connectWebSocketRef.current = connectWebSocket;

  const retryConnection = useCallback(() => {
    const retrySupportedStatus = [
      CONNECTION_STATUS.MARKET_CLOSED,
      CONNECTION_STATUS.DISCONNECTED,
      CONNECTION_STATUS.FAILED,
    ].includes(connectionStatus);
    const currentSocket = socketRef.current;
    const socketAlreadyActive = currentSocket && (
      currentSocket.readyState === WebSocket.CONNECTING ||
      currentSocket.readyState === WebSocket.OPEN
    );

    if (
      !retrySupportedStatus ||
      !getLatestJwt() ||
      disposedRef.current ||
      logoutRef.current ||
      socketAlreadyActive ||
      connectionStatus === CONNECTION_STATUS.LOGGED_OUT
    ) {
      if (logoutRef.current || connectionStatus === CONNECTION_STATUS.LOGGED_OUT) {
        setConnectionStatus(CONNECTION_STATUS.LOGGED_OUT);
      }
      return null;
    }

    if (logoutRef.current || connectionStatus === CONNECTION_STATUS.LOGGED_OUT) {
      setConnectionStatus(CONNECTION_STATUS.LOGGED_OUT);
      return null;
    }

    const wasMarketClosed = marketClosedRef.current;
    marketClosedRef.current = false;
    reconnectAttemptsRef.current = 0;
    clearReconnectTimer();

    if (wasMarketClosed && currentSocket) {
      stopHeartbeat();
      connectionGenerationRef.current += 1;
      socketRef.current = null;
      currentSocket.onopen = null;
      currentSocket.onmessage = null;
      currentSocket.onerror = null;
      currentSocket.onclose = null;
      if (
        currentSocket.readyState === WebSocket.OPEN ||
        currentSocket.readyState === WebSocket.CONNECTING
      ) {
        currentSocket.close();
      }
    }

    return connectWebSocket({ reconnect: false });
  }, [clearReconnectTimer, connectWebSocket, connectionStatus, stopHeartbeat]);

  const canRetryConnection = useMemo(() => {
    const retrySupportedStatus = [
      CONNECTION_STATUS.MARKET_CLOSED,
      CONNECTION_STATUS.DISCONNECTED,
      CONNECTION_STATUS.FAILED,
    ].includes(connectionStatus);
    const currentSocket = socketRef.current;
    const socketAlreadyActive = currentSocket && (
      currentSocket.readyState === WebSocket.CONNECTING ||
      currentSocket.readyState === WebSocket.OPEN
    );

    return Boolean(
      retrySupportedStatus &&
      getLatestJwt() &&
      !disposedRef.current &&
      !logoutRef.current &&
      !socketAlreadyActive
    );
  }, [connectionStatus]);

  const logoutCurrentSocket = useCallback(() => {
    if (logoutPromiseRef.current) return logoutPromiseRef.current;

    clearLogoutCloseTimer();
    logoutRef.current = true;
    marketClosedRef.current = false;
    clearReconnectTimer();
    stopHeartbeat();
    desiredSourcesRef.current = createDesiredSubscriptionState();
    processedTradeClosesRef.current.clear();
    setConnectionStatus(CONNECTION_STATUS.LOGGED_OUT);

    const ws = socketRef.current;
    const currentGeneration = connectionGenerationRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      safeSendSocketPayload({
        ws,
        generation: currentGeneration,
        payload: { action: 'user_logout' },
        closeOnFailure: false,
      });
    }

    const logoutGeneration = connectionGenerationRef.current + 1;
    connectionGenerationRef.current = logoutGeneration;
    logoutFinishGenerationRef.current = logoutGeneration;
    socketRef.current = null;
    logoutSocketRef.current = ws;

    logoutPromiseRef.current = new Promise((resolve) => {
      logoutResolveRef.current = resolve;
      const finish = () => finishLogout(logoutGeneration);

      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        finish();
        return;
      }

      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = finish;

      if (ws.readyState === WebSocket.CONNECTING) {
        try {
          ws.close();
        } catch (e) {
          // Logout must not hang on a failed local close.
        }
        finish();
        return;
      }

      logoutCloseTimeoutRef.current = setTimeout(() => {
        logoutCloseTimeoutRef.current = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          try {
            ws.close();
          } catch (e) {
            // Logout must resolve even when local close throws.
          }
        }
        finish();
      }, LOGOUT_CLOSE_TIMEOUT_MS);
    });

    return logoutPromiseRef.current;
  }, [clearLogoutCloseTimer, clearReconnectTimer, finishLogout, safeSendSocketPayload, stopHeartbeat]);

  useEffect(() => (
    registerAuthSocketCloser(logoutCurrentSocket)
  ), [logoutCurrentSocket]);

  useEffect(() => {
    disposedRef.current = false;
    const lifecycleGeneration = lifecycleGenerationRef.current + 1;
    lifecycleGenerationRef.current = lifecycleGeneration;
    connectWebSocket();

    return () => {
      if (lifecycleGeneration !== lifecycleGenerationRef.current) return;

      disposedRef.current = true;
      clearReconnectTimer();
      clearLogoutCloseTimer();
      stopHeartbeat();
      connectionGenerationRef.current += 1;
      logoutFinishGenerationRef.current += 1;

      const ws = socketRef.current;
      socketRef.current = null;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }

      const logoutWs = logoutSocketRef.current;
      logoutSocketRef.current = null;
      if (logoutWs) {
        logoutWs.onopen = null;
        logoutWs.onmessage = null;
        logoutWs.onerror = null;
        logoutWs.onclose = null;
        if (logoutWs.readyState === WebSocket.OPEN || logoutWs.readyState === WebSocket.CONNECTING) {
          logoutWs.close();
        }
      }

      const resolveLogout = logoutResolveRef.current;
      logoutResolveRef.current = null;
      logoutPromiseRef.current = null;
      if (resolveLogout) resolveLogout();
    };
  }, [clearLogoutCloseTimer, clearReconnectTimer, connectWebSocket, stopHeartbeat]);

  const getTicks = useCallback(() => {
    return tickMapRef.current;
  }, []);

  const setGlobalTokens = useCallback((tokens = []) => {
    if (logoutRef.current) return null;
    return sendDesiredPayload(setGlobalDesired(desiredSourcesRef.current, tokens));
  }, [sendDesiredPayload]);

  const setActiveWatchlist = useCallback((watchlistId) => {
    if (logoutRef.current) return null;
    const id = parsePositiveSafeInteger(watchlistId);
    if (!id) return null;
    // Idempotent: same active watchlist must not re-send switch_watchlist (e.g. trade-close loops).
    const current = desiredSourcesRef.current.get(SOURCE_KEYS.ACTIVE_WATCHLIST);
    if (current?.watchlistId === id) {
      return current.payload;
    }
    return sendDesiredPayload(setActiveWatchlistDesired(desiredSourcesRef.current, watchlistId));
  }, [sendDesiredPayload]);

  const clearActiveWatchlist = useCallback((watchlistId) => {
    if (logoutRef.current) return null;
    return sendDesiredPayload(clearActiveWatchlistDesired(desiredSourcesRef.current, watchlistId));
  }, [sendDesiredPayload]);

  const enableOpenTrades = useCallback(() => {
    if (logoutRef.current) return null;
    return sendDesiredPayload(enableOpenTradesDesired(desiredSourcesRef.current));
  }, [sendDesiredPayload]);

  const disableOpenTrades = useCallback(() => {
    if (logoutRef.current) return null;
    return sendDesiredPayload(disableOpenTradesDesired(desiredSourcesRef.current));
  }, [sendDesiredPayload]);

  const setManualModalTokens = useCallback((tokens = []) => {
    if (logoutRef.current) return null;
    return sendDesiredPayload(setManualModalDesired(desiredSourcesRef.current, tokens));
  }, [sendDesiredPayload]);

  const clearManualModalTokens = useCallback(() => {
    if (logoutRef.current) return null;
    return sendDesiredPayload(clearManualModalDesired(desiredSourcesRef.current));
  }, [sendDesiredPayload]);

  const inspectDesiredSources = useCallback(() => (
    snapshotDesiredSources(desiredSourcesRef.current)
  ), []);

  const subscribe = useCallback((message, tokens, watchlistId) => {
    if (logoutRef.current) return null;

    const { payload } = applyGenericSubscribeAction(
      desiredSourcesRef.current,
      message,
      tokens,
      watchlistId
    );

    sendPayload(payload);
    return payload;
  }, [sendPayload]);

  const refreshActiveWatchlist = useCallback((watchlistId) => {
    if (logoutRef.current) return null;
    const id = Number(watchlistId);
    if (!Number.isSafeInteger(id) || id <= 0) return null;
    return sendDesiredPayload({
      action: 'refresh_active_watchlist',
      watchlistId: id,
    });
  }, [sendDesiredPayload]);

  const contextValue = useMemo(() => ({
    subscribe,
    getTicks,
    connectionStatus,
    subscriptionStatus,
    activeWatchlistTokens,
    symbolStatuses,
    getSymbolStatus,
    getSymbolStatuses,
    registerPendingSymbol,
    setGlobalTokens,
    setActiveWatchlist,
    clearActiveWatchlist,
    refreshActiveWatchlist,
    enableOpenTrades,
    disableOpenTrades,
    setManualModalTokens,
    clearManualModalTokens,
    inspectDesiredSources,
    retryConnection,
    canRetryConnection,
    logoutCurrentSocket,
  }), [
    subscribe,
    getTicks,
    connectionStatus,
    subscriptionStatus,
    activeWatchlistTokens,
    symbolStatuses,
    getSymbolStatus,
    getSymbolStatuses,
    registerPendingSymbol,
    setGlobalTokens,
    setActiveWatchlist,
    clearActiveWatchlist,
    refreshActiveWatchlist,
    enableOpenTrades,
    disableOpenTrades,
    setManualModalTokens,
    clearManualModalTokens,
    inspectDesiredSources,
    retryConnection,
    canRetryConnection,
    logoutCurrentSocket,
  ]);

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
